import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { colors } from '../constants/colors';
import {
  blockUser,
  unblockUser,
  kickFromShow,
  banUser,
  unbanUser,
  checkUserBanned,
  getOrCreateDmRoom,
} from '../api/client';

interface UserActionMenuProps {
  user: { id: string; name: string } | null;
  /** Position in the parent container's coordinate space (parent must be
   *  position:relative). */
  position: { top: number; left: number };
  /** When true, show seller-only actions (kick/ban). */
  isSeller: boolean;
  /** Required for the kick action. */
  showId?: string;
  onClose: () => void;
}

export function UserActionMenu({
  user,
  position,
  isSeller,
  showId,
  onClose,
}: UserActionMenuProps) {
  const navigate = useNavigate();
  const [userBlocked, setUserBlocked] = useState(false);
  const [userBanned, setUserBanned] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Reset toggles when the menu opens for a different user
  useEffect(() => {
    if (!user) return;
    setUserBlocked(false);
    setUserBanned(false);
  }, [user?.id]);

  // Check ban status when menu opens for a seller
  useEffect(() => {
    if (!user || !isSeller) return;
    checkUserBanned(user.id).then(setUserBanned).catch(() => setUserBanned(false));
  }, [user?.id, isSeller]);

  // Close on outside click
  useEffect(() => {
    if (!user) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-user-menu]')) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [user, onClose]);

  const handleAction = useCallback(async (action: string) => {
    if (!user) return;
    setActionLoading(true);
    try {
      switch (action) {
        case 'block':
          await blockUser(user.id);
          setUserBlocked(true);
          break;
        case 'unblock':
          await unblockUser(user.id);
          setUserBlocked(false);
          break;
        case 'kick':
          if (showId) await kickFromShow(showId, user.id);
          break;
        case 'ban':
          await banUser(user.id);
          setUserBanned(true);
          break;
        case 'unban':
          await unbanUser(user.id);
          setUserBanned(false);
          break;
        case 'message':
          try {
            await getOrCreateDmRoom(user.id);
            onClose();
            navigate('/inbox');
          } catch (err) {
            console.error('Failed to open DM:', err);
          }
          break;
      }
    } catch (err) {
      console.error(`Failed to ${action} user:`, err);
    } finally {
      setActionLoading(false);
      // Block/ban toggles stay open so the user can see the resulting state;
      // kick/message close the menu after the action.
      const stayOpen = ['block', 'unblock', 'ban', 'unban'].includes(action);
      if (!stayOpen && action !== 'message') {
        onClose();
      }
    }
  }, [user, showId, navigate, onClose]);

  if (!user) return null;

  return (
    <div
      data-user-menu
      style={{
        ...styles.menu,
        top: position.top + 4,
        left: Math.min(position.left, 200),
      }}
    >
      <div style={styles.menuHeader}>{user.name}</div>

      <button
        style={styles.menuItem}
        onClick={() => handleAction(userBlocked ? 'unblock' : 'block')}
        disabled={actionLoading}
      >
        {userBlocked ? 'Unblock User' : 'Block User'}
      </button>

      {isSeller && (
        <>
          <button
            style={styles.menuItem}
            onClick={() => handleAction('kick')}
            disabled={actionLoading}
          >
            Kick from Show
          </button>
          <button
            style={styles.menuItem}
            onClick={() => handleAction(userBanned ? 'unban' : 'ban')}
            disabled={actionLoading}
          >
            {userBanned ? 'Unban from Shows' : 'Ban from Shows'}
          </button>
        </>
      )}

      <button
        style={styles.menuItem}
        onClick={() => handleAction('message')}
        disabled={actionLoading}
      >
        Message User
      </button>

      <button
        style={{ ...styles.menuItem, color: colors.gray[400] }}
        onClick={onClose}
      >
        Close
      </button>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  menu: {
    position: 'absolute',
    zIndex: 50,
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
    minWidth: '160px',
    padding: '4px 0',
  },
  menuHeader: {
    padding: '8px 12px',
    fontSize: '13px',
    fontWeight: '700',
    color: '#1f2937',
    borderBottom: '1px solid #e5e7eb',
  },
  menuItem: {
    display: 'block',
    width: '100%',
    padding: '8px 12px',
    background: 'none',
    border: 'none',
    fontSize: '13px',
    color: '#374151',
    textAlign: 'left',
    cursor: 'pointer',
  },
};
